import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { GET, httpOk } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'

import ThermalPrint, { ThermalPrintButton, useThermalPrint } from 'pages/parts/ThermalPrint'

function Preview() {
	const { t } = useTranslation()
	const { id } = useParams()

	const [data, setData] = useState({})
	const { printRef, handlePrint } = useThermalPrint()

	async function getData() {
		const response = await GET(`/documents-breakdown/${id}`, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		getData()
	}, [id]) // eslint-disable-line react-hooks/exhaustive-deps

	const source = data.product
	const outputs = source?.outputs || []

	function sourceAmountDisplay() {
		if (!source) return 0
		const q = Number(source.quantity) || 0
		const c = Number(source.cost_price) || 0
		if (source.total_cost_price != null && source.total_cost_price !== '') {
			return Number(source.total_cost_price)
		}
		return q * c
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate align-items-center flex-wrap gap-2">
					<h4 className="vertical-center mb-0">{t('documents_breakdown')}</h4>
					<div className="vertical-center">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center">
						<h6 className="mb-0">{t('preview')}</h6>
					</div>
				</div>

				<div className="vertical-center">
					<ThermalPrintButton onClick={handlePrint} />
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h4 className="fw-600">
						{t('document_from')}{' '}
						{data.created_at ? formatDate(data.created_at, 'dd.MM.yy HH:mm') : '—'}
					</h4>
					<div className="flex-column preview-info">
						<div className="d-flex gap-2">
							<div className="fw-600">{t('pos')}:</div>
							<div>{data.pos_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('from_warehouse')}:</div>
							<div>{data.wms_from_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('to_warehouse')}:</div>
							<div>{data.wms_to_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('note')}:</div>
							<div>{data.note}</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h5 className="mb-3">{t('breakdown_source_product')}</h5>
					<div style={{ overflow: 'auto' }}>
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th className="text-center" style={{ minWidth: '33px' }}>#</th>
									<th style={{ width: '30%' }}>{t('name2')}</th>
									<th style={{ width: '20%' }}>{t('type')}</th>
									<th style={{ width: '10%' }} className="text-nowrap">{t('unit')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ width: '20%' }} className="text-end text-nowrap">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								{source ? (
									<tr>
										<td className="text-center">1</td>
										<td className="text-break">{source.name ?? '—'}</td>
										<td>{source.product_type_name ?? ''}</td>
										<td>{source.unit_name ?? ''}</td>
										<td className="text-end">{formatMoney(source.quantity)}</td>
										<td className="text-end">{formatMoney(source.cost_price)}</td>
										<td className="text-end text-nowrap">{formatMoney(sourceAmountDisplay())}</td>
									</tr>
								) : (
									<tr>
										<td colSpan={7} className="text-center text-muted">
											—
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h5 className="mb-3">{t('breakdown_outputs')}</h5>
					<div style={{ overflow: 'auto' }}>
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th className="text-center" style={{ minWidth: '33px' }}>#</th>
									<th style={{ width: '30%' }}>{t('name2')}</th>
									<th style={{ width: '20%' }}>{t('type')}</th>
									<th style={{ width: '10%' }} className="text-nowrap">{t('unit')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ width: '20%' }} className="text-end text-nowrap">{t('cost_share_percent')}</th>
								</tr>
							</thead>
							<tbody>
								{outputs.map((row, index) => (
									<tr key={row.id ?? index}>
										<td className="text-center">{index + 1}</td>
										<td className="text-break">{row?.name ?? '—'}</td>
										<td>{row?.product_type_name ?? ''}</td>
										<td>{row?.unit_name ?? ''}</td>
										<td className="text-end">{formatMoney(row?.quantity)}</td>
										<td className="text-end">{formatMoney(row?.cost_price)}</td>
										<td className="text-end">{formatMoney(row?.cost_share_percent, 2)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div className="d-flex justify-content-end gap-2 mt-3">
						<Link to="/documents-breakdown" className="btn btn-warning btn-rounded btn-wide" tabIndex={-1}>
							{t('back')}
						</Link>
					</div>
				</div>
			</div>

			<ThermalPrint
				printRef={printRef}
				title={`${t('document_from')} ${data.created_at ? formatDate(data.created_at, 'dd.MM.yy HH:mm') : ''}`}
				info={[
					{ label: t('pos'), value: data.pos_name },
					{ label: t('from_warehouse'), value: data.wms_from_name },
					{ label: t('to_warehouse'), value: data.wms_to_name },
					{ label: t('note'), value: data.note },
				]}
				sections={[
					{
						title: t('breakdown_source_product'),
						columns: [
							{ label: t('name2'), width: '48%', numbered: true, render: (row) => row.name },
							{ label: t('quantity'), width: '18%', align: 'end', render: (row) => formatMoney(row.quantity, 2) },
							{ label: t('cost_price'), width: '34%', align: 'end', render: (row) => formatMoney(row.cost_price) },
						],
						rows: source ? [source] : [],
					},
					{
						title: t('breakdown_outputs'),
						columns: [
							{ label: t('name2'), width: '48%', numbered: true, render: (row) => row.name },
							{ label: t('quantity'), width: '18%', align: 'end', render: (row) => formatMoney(row.quantity, 2) },
							{ label: t('cost_price'), width: '34%', align: 'end', render: (row) => formatMoney(row.cost_price) },
						],
						rows: outputs,
					},
				]}
			/>
		</>
	)
}

export default Preview
